import { isValidPhoneNumber } from 'libphonenumber-js/min';
import { type PostalCodeLocale, isPostalCode, isVAT } from 'validator';
import {
  type AnyObject,
  type Flags,
  type LocaleObject,
  type Maybe,
  type Message,
  type Schema,
  addMethod,
  setLocale,
  string,
} from 'yup';

declare module 'yup' {
  interface StringSchema<
    TType extends Maybe<string> = string | undefined,
    TContext extends AnyObject = AnyObject,
    TDefault = undefined,
    TFlags extends Flags = '',
  > extends Schema<TType, TContext, TDefault, TFlags> {
    isPostalCode(locale: PostalCodeLocale | 'any'): this;

    isVAT(countryCode: string): this;

    isVATLengthCorrect(countryCode: PostalCodeLocale): this;

    isStreetAddress(): this;

    isPhone(locale: PostalCodeLocale): this;
  }
}

const addYupStringValidator = (
  validatorFnName: string,
  translationKey: string,
  // biome-ignore lint: any is needed, because we don't know params types
  testFn: (value: string, ...params: any) => boolean,
) => {
  addMethod(string, validatorFnName, function (...params) {
    return this.test(
      validatorFnName,
      (msg) => ({ translationKey, ...msg }),
      (value = '') => testFn(value, ...params),
    );
  });
};

type TypeKey = keyof LocaleObject;
type ValidatorKey<T extends TypeKey> = keyof NonNullable<LocaleObject[T]> &
  string;

type ExcludedKeys = 'originalValue' | 'spec';
type MessageParams<T extends Message> = Exclude<
  T extends Message<infer TExtra> ? TExtra : never,
  ExcludedKeys
>;

type ValidatorMessageParams<
  TK extends TypeKey,
  VK extends ValidatorKey<TK>,
> = MessageParams<NonNullable<NonNullable<LocaleObject[TK]>[VK]>>;

const msg =
  <TK extends TypeKey, VK extends ValidatorKey<TK>>(
    key: `${TK}.${VK}`,
    translationKey: string,
  ) =>
  (messageParams: ValidatorMessageParams<TK, VK>) =>
    ({ key, translationKey, ...messageParams }) as const;

setLocale({
  mixed: {
    required: msg('mixed.required', 'required'),
  },
  string: {
    min: msg('string.min', 'string.min'),
    max: msg('string.max', 'string.max'),
    email: msg('string.email', 'string.email'),
  },
});

type Defaults<K extends string> = {
  path: string;
  value: unknown;
  translationKey: string;
  message?: string;
  key: K;
  min?: string;
  max?: string;
};

export type ErrorMessageData =
  | Defaults<'mixed.required' | 'string.email'>
  | (Defaults<'string.min'> & { min: string })
  | (Defaults<'string.max'> & { max: string });

export const streetAndNumberPattern = /(.*?)\s+([0-9]+.*)/;

const getChecksumVerifier =
  (modulus: number, weights: number[]) => (value: string) => {
    if (!value) return false;

    const digits = value.split('').slice(0, -1);
    const lastDigit = +(value.at(-1) as unknown as string);
    const sum = digits.reduce(
      (accumulator, digit, i) => accumulator + weights[i] * +digit,
      0,
    );

    return sum % modulus === lastDigit;
  };

const verifyVATChecksumPL = getChecksumVerifier(
  11,
  [6, 5, 7, 2, 3, 4, 5, 6, 7],
);

const vatNumberLengthsByCountry: {
  [key in string]: number | number[];
} = {
  PL: 10,
  LT: [9, 12],
  LV: 11,
  EE: [0, 9, 11],
  FI: 8,
};

const isVatLengthCorrect = (locale: PostalCodeLocale, value: number) => {
  const length = vatNumberLengthsByCountry[locale];
  if (Array.isArray(length)) {
    return length.includes(value);
  }

  return value === length;
};

addYupStringValidator(
  'isVATLengthCorrect',
  'string.vat_number_length',
  (value, countryCode) =>
    isVatLengthCorrect(countryCode as PostalCodeLocale, value.length),
);

addYupStringValidator('isPostalCode', 'string.postal_code', isPostalCode);

addYupStringValidator('isVAT', 'string.vat_number', (value, countryCode) => {
  if (countryCode === 'EE') {
    if (
      (value.startsWith('EE') || value.startsWith('ee')) &&
      value.length === 11
    ) {
      return true;
    } else if (value.length === 0) {
      return true;
    } else {
      return isVAT(value, countryCode);
    }
  } else {
    return (
      isVAT(value, countryCode) &&
      (countryCode !== 'PL' || verifyVATChecksumPL(value))
    );
  }
});

addYupStringValidator(
  'isStreetAddress',
  'string.street_address',
  (value) => !!value.match(streetAndNumberPattern),
);

addYupStringValidator('isPhone', 'string.phone', (value, locale) =>
  isValidPhoneNumber(value, locale),
);
